/**
 * useFeatureFlags Hook
 *
 * A React hook that provides convenient access to MVP feature flags configuration.
 * Centralizes feature flag checks for components to easily determine what's enabled.
 *
 * Supports runtime bypass mode via featureFlagService - when bypass is enabled,
 * all feature checks return true (Demo Mode).
 *
 * @version 1.1.0
 * @date January 2026
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  isMVPMode,
  isFeatureEnabled as isFeatureEnabledConfig,
  isApplicationEnabled as isApplicationEnabledConfig,
  isObjectEnabled as isObjectEnabledConfig,
  isObjectEnabledByApiName as isObjectEnabledByApiNameConfig,
  isAdminFeatureEnabled as isAdminFeatureEnabledConfig,
  isNavItemEnabled as isNavItemEnabledConfig,
  isNavGroupEnabled as isNavGroupEnabledConfig,
  isNavItemHidden as isNavItemHiddenConfig,
  isRouteDisabled as isRouteDisabledConfig,
  shouldBypassRoleChecks,
  getMVPDefaultRole,
  getEnabledApplications,
  getEnabledObjects,
  getEnabledNavItems,
  getEnabledNavGroups,
  MVP_CONFIG,
  type ApplicationId,
  type ObjectName,
  type AdminFeature,
  type NavigationItem,
  type NavigationGroup,
  type FeatureFlags
} from '../config/mvpConfig';
import {
  isBypassModeEnabled,
  subscribeToBypassModeChanges
} from '../services/featureFlagService';

/**
 * Feature flags hook return type
 */
export interface UseFeatureFlagsReturn {
  // State
  /** Whether MVP mode is currently active */
  mvpMode: boolean;
  /** Whether RBAC checks are bypassed (all users = admin) */
  bypassRoleChecks: boolean;
  /** Default role when RBAC bypass is active */
  defaultRole: string;

  // Check functions
  /** Check if a specific feature flag is enabled */
  isFeatureEnabled: (feature: keyof FeatureFlags) => boolean;
  /** Check if an application is enabled */
  isApplicationEnabled: (appId: ApplicationId) => boolean;
  /** Check if an object is enabled by type name */
  isObjectEnabled: (objectName: ObjectName) => boolean;
  /** Check if an object is enabled by API name (handles __c suffix) */
  isObjectEnabledByApiName: (apiName: string) => boolean;
  /** Check if an admin feature is enabled */
  isAdminFeatureEnabled: (feature: AdminFeature) => boolean;
  /** Check if a navigation item is enabled */
  isNavItemEnabled: (item: NavigationItem) => boolean;
  /** Check if a navigation group is enabled */
  isNavGroupEnabled: (group: NavigationGroup) => boolean;
  /** Check if a navigation item should be hidden (by path) */
  isNavItemHidden: (path: string) => boolean;
  /** Check if a route is disabled */
  isRouteDisabled: (path: string) => boolean;

  // List getters
  /** Get list of enabled application IDs */
  enabledApplications: ApplicationId[];
  /** Get list of enabled object names */
  enabledObjects: ObjectName[];
  /** Get list of enabled navigation items */
  enabledNavItems: NavigationItem[];
  /** Get list of enabled navigation groups */
  enabledNavGroups: NavigationGroup[];
  /** Get list of enabled admin features */
  enabledAdminFeatures: AdminFeature[];

  // Direct config access
  /** Full MVP configuration object (for advanced use cases) */
  config: typeof MVP_CONFIG;
}

/**
 * Hook for accessing feature flags configuration
 *
 * Supports runtime bypass mode - when bypass is enabled (Demo Mode),
 * all feature checks return true, unlocking all features.
 *
 * @example
 * ```tsx
 * const { mvpMode, isApplicationEnabled, enabledObjects } = useFeatureFlags();
 *
 * // Check if CyberSweep is enabled
 * if (isApplicationEnabled('cybersweep')) {
 *   // Show CyberSweep app
 * }
 *
 * // Filter objects in a list
 * const visibleObjects = allObjects.filter(obj =>
 *   isObjectEnabledByApiName(obj.apiName)
 * );
 * ```
 */
export function useFeatureFlags(): UseFeatureFlagsReturn {
  // Track bypass mode state - triggers re-render when it changes
  const [bypassMode, setBypassMode] = useState(isBypassModeEnabled);

  // Subscribe to bypass mode changes
  useEffect(() => {
    const unsubscribe = subscribeToBypassModeChanges((enabled: boolean) => {
      setBypassMode(enabled);
    });
    return unsubscribe;
  }, []);

  // Create bypass-aware check functions
  const isFeatureEnabled = useCallback(
    (feature: keyof FeatureFlags) => bypassMode || isFeatureEnabledConfig(feature),
    [bypassMode]
  );

  const isApplicationEnabled = useCallback(
    (appId: ApplicationId) => bypassMode || isApplicationEnabledConfig(appId),
    [bypassMode]
  );

  const isObjectEnabled = useCallback(
    (objectName: ObjectName) => bypassMode || isObjectEnabledConfig(objectName),
    [bypassMode]
  );

  const isObjectEnabledByApiName = useCallback(
    (apiName: string) => bypassMode || isObjectEnabledByApiNameConfig(apiName),
    [bypassMode]
  );

  const isAdminFeatureEnabled = useCallback(
    (feature: AdminFeature) => bypassMode || isAdminFeatureEnabledConfig(feature),
    [bypassMode]
  );

  const isNavItemEnabled = useCallback(
    (item: NavigationItem) => bypassMode || isNavItemEnabledConfig(item),
    [bypassMode]
  );

  const isNavGroupEnabled = useCallback(
    (group: NavigationGroup) => bypassMode || isNavGroupEnabledConfig(group),
    [bypassMode]
  );

  const isNavItemHidden = useCallback(
    (path: string) => bypassMode ? false : isNavItemHiddenConfig(path),
    [bypassMode]
  );

  const isRouteDisabled = useCallback(
    (path: string) => bypassMode ? false : isRouteDisabledConfig(path),
    [bypassMode]
  );

  // Compute enabled lists based on bypass mode
  const enabledApplications = useMemo(() => {
    if (bypassMode) {
      return ['regal-main', 'cybersweep', 'system-admin', 'credit-management', 'credit-block'] as ApplicationId[];
    }
    return getEnabledApplications();
  }, [bypassMode]);

  const enabledObjects = useMemo(() => {
    if (bypassMode) {
      return [
        'Client', 'CyberSweepClient', 'CSAudit', 'PAWRecord',
        'User', 'Note', 'Task', 'Case', 'Event',
        'Lead', 'Contact', 'Opportunity', 'Report'
      ] as ObjectName[];
    }
    return getEnabledObjects();
  }, [bypassMode]);

  const enabledNavItems = useMemo(() => {
    if (bypassMode) {
      return ['home', 'clients', 'leads', 'contacts', 'opportunities', 'cases', 'tasks', 'events', 'dashboard', 'reports', 'system-admin'] as NavigationItem[];
    }
    return getEnabledNavItems();
  }, [bypassMode]);

  const enabledNavGroups = useMemo(() => {
    if (bypassMode) {
      return ['sales', 'billing', 'service', 'productivity', 'analytics'] as NavigationGroup[];
    }
    return getEnabledNavGroups();
  }, [bypassMode]);

  return {
    // State - mvpMode is false when bypass is enabled (Demo Mode)
    mvpMode: bypassMode ? false : isMVPMode(),
    bypassRoleChecks: shouldBypassRoleChecks(),
    defaultRole: getMVPDefaultRole(),

    // Check functions (bypass-aware)
    isFeatureEnabled,
    isApplicationEnabled,
    isObjectEnabled,
    isObjectEnabledByApiName,
    isAdminFeatureEnabled,
    isNavItemEnabled,
    isNavGroupEnabled,
    isNavItemHidden,
    isRouteDisabled,

    // List getters (bypass-aware)
    enabledApplications,
    enabledObjects,
    enabledNavItems,
    enabledNavGroups,
    enabledAdminFeatures: MVP_CONFIG.enabledAdminFeatures,

    // Direct config access
    config: MVP_CONFIG,
  };
}

/**
 * Convenience hook for just checking MVP mode
 */
export function useMVPMode(): boolean {
  const [bypassEnabled, setBypassEnabled] = useState(isBypassModeEnabled);

  useEffect(() => {
    const unsubscribe = subscribeToBypassModeChanges((enabled: boolean) => {
      setBypassEnabled(enabled);
    });
    return unsubscribe;
  }, []);

  return useMemo(() => isMVPMode(), [bypassEnabled]);
}

/**
 * Convenience hook for checking if RBAC is bypassed
 */
export function useBypassRBAC(): boolean {
  const [bypassEnabled, setBypassEnabled] = useState(isBypassModeEnabled);

  useEffect(() => {
    const unsubscribe = subscribeToBypassModeChanges((enabled: boolean) => {
      setBypassEnabled(enabled);
    });
    return unsubscribe;
  }, []);

  return useMemo(() => shouldBypassRoleChecks(), [bypassEnabled]);
}

export default useFeatureFlags;
